import React, { useCallback, useEffect, useRef, useState } from "react";
import { RiArrowLeftSLine, RiArrowRightSLine, RiCloseLine } from "react-icons/ri";
import { listAll, getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../../firebaseConfig";
import localGalleryImages from "../../lib/localGallery";
import Reveal from "../../components/reveal/Reveal";
import { setScrollLocked } from "../../lib/motion";
import "./gallery.css";

const shuffle = (input) => {
  const array = [...input];
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const SKELETON_COUNT = 6;

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingLocal, setUsingLocal] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const railRef = useRef(null);
  const lightboxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fallBackToBundled = () => {
      if (cancelled) return;
      setImages(shuffle(localGalleryImages));
      setUsingLocal(true);
      setLoading(false);
    };

    const load = async () => {
      try {
        const listing = await listAll(storageRef(storage, "blog_images"));
        const urls = await Promise.all(
          listing.items.map((item) => getDownloadURL(item))
        );
        if (cancelled) return;
        if (urls.length) {
          setImages(shuffle(urls));
          setUsingLocal(false);
          setLoading(false);
        } else {
          fallBackToBundled();
        }
      } catch {
        fallBackToBundled();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollRail = useCallback((direction) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.8, 240),
      behavior: "smooth",
    });
  }, []);

  const step = useCallback(
    (delta) => {
      setLightbox((current) => {
        if (current === null) return current;
        const next = (current + delta + images.length) % images.length;
        return next;
      });
    },
    [images.length]
  );

  useEffect(() => {
    if (lightbox === null) return undefined;
    document.body.classList.add("is-locked");
    setScrollLocked(true);
    const onKeyDown = (event) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKeyDown);
    lightboxRef.current?.focus();
    return () => {
      document.body.classList.remove("is-locked");
      setScrollLocked(false);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [lightbox, step]);

  return (
    <section className="section gallery" aria-labelledby="gallery-title">
      <div className="shell">
        <p className="marker">
          <span className="marker__num">03</span>
          <span>From the field</span>
        </p>

        <Reveal className="gallery__head">
          <div>
            <h2 id="gallery-title" className="t-h2">
              Photographs from our programmes
            </h2>
            <p className="gallery__sub">
              Taken by our own team during sessions, camps and school visits.
            </p>
          </div>
          <div className="gallery__controls">
            <button
              type="button"
              className="rail-btn"
              onClick={() => scrollRail(-1)}
              aria-label="Scroll photographs left"
            >
              <RiArrowLeftSLine size={22} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rail-btn"
              onClick={() => scrollRail(1)}
              aria-label="Scroll photographs right"
            >
              <RiArrowRightSLine size={22} aria-hidden="true" />
            </button>
          </div>
        </Reveal>
      </div>

      <div className="gallery__rail-wrap">
        <div
          className="rail gallery__rail shell"
          ref={railRef}
          tabIndex={0}
          role="group"
          aria-label="Photographs, scroll horizontally"
        >
          {loading
            ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <div className="gallery__card skeleton" key={`s-${i}`} />
              ))
            : images.map((src, index) => (
                <button
                  type="button"
                  className="gallery__card"
                  key={`${src}-${index}`}
                  onClick={() => setLightbox(index)}
                  aria-label={`Open photograph ${index + 1} of ${images.length}`}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                      event.currentTarget.closest(".gallery__card").hidden = true;
                    }}
                  />
                </button>
              ))}
        </div>
      </div>

      {usingLocal && !loading ? (
        <div className="shell">
          <p className="caption gallery__note">
            Showing photographs saved with the site — our live photo library is
            not reachable right now.
          </p>
        </div>
      ) : null}

      {lightbox !== null ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photograph viewer"
          ref={lightboxRef}
          tabIndex={-1}
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="lightbox__close"
            aria-label="Close photograph"
            onClick={() => setLightbox(null)}
          >
            <RiCloseLine size={22} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            aria-label="Previous photograph"
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
          >
            <RiArrowLeftSLine size={22} aria-hidden="true" />
          </button>
          <img
            className="lightbox__image"
            src={images[lightbox]}
            alt={`Photograph ${lightbox + 1} of ${images.length}`}
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            aria-label="Next photograph"
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
          >
            <RiArrowRightSLine size={22} aria-hidden="true" />
          </button>
          <p className="lightbox__count">
            {lightbox + 1} / {images.length}
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default Gallery;
